import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { apiErrorMessage } from "../../lib/apiError";
import { GST_REG_TYPES, REASON_MIN, displayRegType, reasonValid, classifySupplierGst } from "../../lib/supplierGstClass";

function History({ items }) {
  if (!items?.length) return <div className="text-[11px] text-zinc-500" data-testid="gst-class-history-empty">No classification history.</div>;
  return (
    <ul className="space-y-1 max-h-32 overflow-y-auto" data-testid="gst-class-history">
      {[...items].reverse().map((h, i) => (
        <li key={i} className="text-[11px] font-mono text-zinc-400" data-testid={`gst-class-history-${i}`}>
          {h.from} → <span className="text-zinc-200">{h.to}</span> · {h.by} · {(h.at || "").slice(0, 19).replace("T", " ")} · {h.reason}
        </li>
      ))}
    </ul>
  );
}

export default function SupplierGstClassDialog({ supplier, open, onOpenChange, onSaved }) {
  const current = displayRegType(supplier);
  const [type, setType] = useState(current);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { if (open) { setType(current); setReason(""); setErr(""); } }, [open, current]);
  const ok = reasonValid(reason) && !busy;

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const saved = await classifySupplierGst(api, supplier.id, type, reason);
      toast.success(`GST registration type set to ${displayRegType(saved)}`);
      onSaved?.(saved);
      onOpenChange(false);
    } catch (e) {
      const m = apiErrorMessage(e, "Classification failed");
      setErr(m); toast.error(m);
    } finally { setBusy(false); }
  };

  if (!supplier) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="gst-class-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle>GST Registration Type · {supplier.name}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div data-testid="gst-class-current">Current: <span className="font-mono font-bold">{current}</span></div>
          {supplier.gst_classification_confirmed_at && (
            <div className="text-[11px] font-mono text-zinc-400" data-testid="gst-class-confirmed">
              Confirmed by {supplier.gst_classification_confirmed_by} · {supplier.gst_classification_confirmed_at.slice(0, 19).replace("T", " ")}
            </div>
          )}
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-zinc-500">New type</span>
            <select data-testid="gst-class-select" value={type} onChange={(e) => setType(e.target.value)}
              className="mt-1 w-full h-9 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm px-2">
              {GST_REG_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-zinc-500">Reason (min {REASON_MIN} characters)</span>
            <textarea data-testid="gst-class-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
              className="mt-1 w-full bg-[var(--bg-0)] border border-[var(--border)] rounded-sm px-2 py-1" />
          </label>
          {!reasonValid(reason) && reason.length > 0 && <div className="text-[11px] text-amber-300" data-testid="gst-class-reason-error">Reason must be at least {REASON_MIN} characters.</div>}
          {err && <div className="text-[11px] text-rose-300" data-testid="gst-class-error">{err}</div>}
          <div><div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">History</div><History items={supplier.gst_classification_history} /></div>
        </div>
        <DialogFooter>
          <button data-testid="gst-class-cancel" onClick={() => onOpenChange(false)} className="h-9 px-4 border border-[var(--border)] rounded-sm">Cancel</button>
          <button data-testid="gst-class-save" disabled={!ok} onClick={save}
            className="h-9 px-4 rounded-sm bg-[var(--accent)] text-black font-bold disabled:opacity-40">{busy ? "Saving…" : "Save"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
